import React from 'react'
import { useTheme } from 'styled-components'
import type { AppTheme } from 'styled-components'

import { ImageLink } from '@definitions/types/storyblokTypes'
import { PropsWithClassName } from '@helper/PropsWithClassName'
import { editable } from '@helper/storyblokEditable'
import { getUrlFromLink } from '@helper/utils/url'

import { StyledImage } from './StoryblokImageLink.styles'

interface Props extends PropsWithClassName {
  blok: ImageLink
  style?: React.CSSProperties
  width?: number
}

const DEFAULT_WIDTH = 270
const DEFAULT_HEIGHT = 80

const StoryblokImageLink = (props: Props): React.ReactNode => {
  const { blok } = props
  const theme = useTheme() as AppTheme
  const link = getUrlFromLink(blok.link, theme)
  const cssVarWidth = props.width ? `${props.width}px` : undefined
  const wrapperStyle: React.CSSProperties = {
    ...props.style,
    ...(cssVarWidth && { ['--logo-w' as string]: cssVarWidth }),
  }

  if (!blok.image?.filename) {
    return null
  }

  const image = (
    <StyledImage
      src={blok.image.filename}
      alt={blok.alt ?? ''}
      width={DEFAULT_WIDTH}
      height={DEFAULT_HEIGHT}
      sizes="(max-width: 768px) 40vw, 270px"
    />
  )

  if (!link) {
    return (
      <span
        {...editable(blok)}
        key={blok._uid}
        className={props.className}
        data-kind="img-link"
        style={wrapperStyle}
      >
        {image}
      </span>
    )
  }

  return (
    <a
      {...editable(blok)}
      key={blok._uid}
      className={props.className}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      data-kind="img-link"
      style={wrapperStyle}
    >
      {image}
    </a>
  )
}

export { StoryblokImageLink }
